import React from "react";
// eslint-disable-next-line
import { MouseEvent, useEffect, useMemo, useState } from "react";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
  Chip,
  SnackbarProps,
  Stack,
  Tooltip,
  Typography,
  IconButton,
} from "@mui/material";
import _debounce from "lodash/debounce";
// third-party
import { ColumnDef } from "@tanstack/react-table";

import {
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import Loader from "../../../../../components/Loader";
import { FormattedMessage } from "react-intl";
import { USERAXIOS } from "../../../../../api/userAxios";
import ReusableTable from "../../../../../components/ReusableComponent/ReusableTable";
import { openSnackbar } from "../../../../../api/snackbar";
import ReusableDeleteDialog from "../../../../../components/ReusableComponent/ReusableDeleteDialog";

import AddEditCompanyReply from "./AddEditCompanyReply";
import { CompanyReplyData } from "../../../../../types/companies-managment/companyReply";
import ExpandingDetail from "./ExpandingDetail";

const CompanyReplies = ({ parentNumber }: { parentNumber?: number }) => {
  const theme = useTheme();
  const [open, setOpen] = useState<boolean>(false);

  const [customerModal, setCustomerModal] = useState<boolean>(false);
  const [selectedCustomer, setSelectedCustomer] =
    useState<CompanyReplyData | null>(null);
  const [selected, setSelected] = useState<any>("");
  const [expandedRowId, setExpandedRowId] = useState<string | number | null>(
    null
  );
  const handleClose = () => {
    setOpen(!open);
  };

  const columns = useMemo<ColumnDef<CompanyReplyData>[]>(
    () => [
      {
        header: () => <FormattedMessage id="ID" />,
        accessorKey: "id",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle1">{row?.original.id}</Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="Resume ID" />,
        accessorKey: "resume_id",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {row?.original.resume_id || "-"}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="Reply Applicant ID" />,
        accessorKey: "reply_to_applicant_id",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {row?.original.reply_to_applicant_id || "-"}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="company" />,
        accessorKey: "company",
        meta: {
          className: "cell-center",
        },
        cell: (cell) => {
          const company = cell.getValue() as any;
          return <Typography>{company?.name || "-"}</Typography>;
        },
      },
      {
        header: () => <FormattedMessage id="User" />,
        accessorKey: "user",
        meta: {
          className: "cell-center",
        },
        cell: (cell) => {
          const user = cell.getValue() as any;
          return <Typography>{user?.user_name || "-"}</Typography>;
        },
      },
      {
        header: () => <FormattedMessage id="Vacancy" />,
        accessorKey: "vacancy",
        meta: {
          className: "cell-center",
        },
        cell: (cell) => {
          const vacancy = cell.getValue() as any;
          return <Typography>{vacancy?.title || "-"}</Typography>;
        },
      },

      // {
      //   header: () => <FormattedMessage id="From User ID" />,
      //   accessorKey: "from_user_id",
      //   meta: {
      //     className: "cell-center",
      //   },
      //   cell: ({ row, getValue }) => (
      //     <Stack direction="row" spacing={1.5} alignItems="center">
      //       <Stack spacing={0}>
      //         <Typography variant="subtitle2">
      //           {getValue() || "Not set"}
      //         </Typography>
      //       </Stack>
      //     </Stack>
      //   ),
      // },
      // {
      //   header: () => <FormattedMessage id="Status" />,
      //   accessorKey: "status",
      //   meta: {
      //     className: "cell-center",
      //   },
      //   cell: (cell) => {
      //     const status = cell.getValue();
      //     return (
      //       <Chip
      //         color={status ? "success" : "error"}
      //         label={
      //           status ? (
      //             <FormattedMessage id="Active" />
      //           ) : (
      //             <FormattedMessage id="Inactive" />
      //           )
      //         }
      //         size="small"
      //         variant="filled"
      //       />
      //     );
      //   },
      // },
      {
        header: () => <FormattedMessage id="Actions" />,
        accessorKey: "actions",
        meta: { className: "cell-center" },
        disableSortBy: true,
        cell: ({ row }) => {
          const isExpanded = String(expandedRowId) === String(row.original.id);
          const collapseIcon = isExpanded ? (
            <CloseOutlined
              style={{
                color: theme.palette.error.main,
              }}
            />
          ) : (
            <EyeOutlined />
          );
          return (
            <Stack direction="row" spacing={0}>
              <Tooltip title={isExpanded ? "close details" : "view details"}>
                <IconButton
                  color="primary"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    setExpandedRowId(
                      isExpanded ? null : String(row.original.id)
                    );
                  }}
                  sx={{
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  {collapseIcon}
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton
                  color="primary"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    setSelected(row.original);
                    setSelectedCustomer(row.original);
                    setCustomerModal(true);
                  }}
                  sx={{
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <EditOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleClose();
                    setSelected(row.original);
                  }}
                >
                  <DeleteOutlined />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    // eslint-disable-next-line
    [theme, expandedRowId]
  );
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CompanyReplyData[]>([]);
  const [countPage, setCountPage] = useState({
    current_page: 0,
    last_page: 0,
  });
  const [currentPage, setCurrenttPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchedtext, setSearchedtext] = useState("");
  const [loadingSearsh, setloadingSearsh] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!selected?.id) return;
    setIsDeleting(true);
    try {
      await USERAXIOS.delete(`/company_replies/${selected?.id}`);
      setResult((prevResults: any[]) =>
        prevResults.filter((item) => item.id !== selected?.id)
      );
      openSnackbar({
        open: true,
        message: "Company reply deleted successfully",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        close: false,
        alert: {
          color: "success",
        },
      } as SnackbarProps);
      if (handleClose) handleClose();
    } catch (error: any) {
      openSnackbar({
        open: true,
        message:
          error?.response?.data?.message || "Failed to delete company reply",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setIsDeleting(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await USERAXIOS.get(
        `/company_replies?page=${currentPage}${
          searchQuery ? `&title=${searchQuery}` : ""
        }`
      );
      const { data, meta } = response.data;
      setResult(data);
      setCountPage(meta);
    } catch (error) {
      openSnackbar({
        open: true,
        message: "Error fetching company replies",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, searchQuery]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrenttPage(page);
  };

  const handleSearch = () => {
    setloadingSearsh(true);
    setCurrenttPage(1);
    setSearchQuery(searchedtext);
    setloadingSearsh(false);
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <ReusableTable
          data={result}
          columns={columns}
          countPage={countPage}
          handlePageChange={handlePageChange}
          handleSearchChange={handleSearch}
          currentPage={currentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          setCurrenttPage={setCurrenttPage}
          loadingSearsh={loadingSearsh}
          searchedtext={searchedtext}
          setSearchedtext={setSearchedtext}
          modalToggler={() => {
            setCustomerModal(true);
            setSelectedCustomer(null);
          }}
          renderDetailPanel={({ row }: any) => (
            <ExpandingDetail data={row.original} />
          )}
          expandedRowId={expandedRowId}
          setExpandedRowId={setExpandedRowId}
        />
      )}

      <ReusableDeleteDialog
        title={selected?.name}
        open={open}
        handleClose={handleClose}
        handleDelete={handleDelete}
        isDeleting={isDeleting}
      />

      <AddEditCompanyReply
        open={customerModal}
        setResult={setResult}
        loading={formLoading}
        setLoading={setFormLoading}
        fetchData={result}
        id={selected}
        companyReply={selectedCustomer}
        handleClose={() => setCustomerModal(false)}
      />
    </>
  );
};

export default CompanyReplies;
